import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface SubscriptionPaymentSectionProps {
  trade: string;
  userId: string;
  profileId: string;
  onPaymentComplete: () => void;
  onWorkwearPurchase?: () => void;
  workwearPurchased?: boolean;
}

const tradePricing: any = {
  'handyman': { yearly: 816, original: 1020 },
  'electrician': { yearly: 1056, original: 1320 },
  'plumber': { yearly: 1152, original: 1440 },
  'waste': { yearly: 768, original: 960 }
};

const SubscriptionPaymentSection: React.FC<SubscriptionPaymentSectionProps> = ({
  trade,
  userId,
  profileId,
  onPaymentComplete,
  onWorkwearPurchase,
  workwearPurchased = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [error, setError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<{
    code: string;
    type: 'percentage' | 'fixed';
    value: number;
  } | null>(null);
  const [discountError, setDiscountError] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  const WORKWEAR_PRICE = 10800; // £108 in cents
  const VAT_RATE = 0.20;
  const WORKWEAR_WITH_VAT = Math.round(WORKWEAR_PRICE * (1 + VAT_RATE)); // £129.60

  // Normalize trade name to lowercase for pricing lookup
  const normalizedTrade = trade.toLowerCase().replace(/\s+/g, '-').replace('waste-removal', 'waste');
  const pricing = tradePricing[normalizedTrade] || tradePricing['handyman'];
  const amount = pricing.yearly;
  const vat = amount * 0.2;
  const total = amount + vat;

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code');
      return;
    }

    setApplyingDiscount(true);
    setDiscountError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch discount code with case-insensitive search
      const { data: codes, error: fetchError } = await supabase
        .from('discount_codes')
        .select('*')
        .ilike('code', discountCode.trim())
        .eq('is_active', true)
        .single();

      if (fetchError || !codes) {
        setDiscountError('Invalid or expired discount code');
        setApplyingDiscount(false);
        return;
      }

      // Check if code applies to provider subscriptions
      const validAppliesTo = ['provider_subscription', 'all_purchases'];
      if (!validAppliesTo.includes(codes.applies_to)) {
        setDiscountError('This code cannot be used for provider subscriptions');
        setApplyingDiscount(false);
        return;
      }

      // Check expiry
      if (codes.expires_at && new Date(codes.expires_at) < new Date()) {
        setDiscountError('This discount code has expired');
        setApplyingDiscount(false);
        return;
      }

      // Check usage limit
      if (codes.max_uses && codes.times_used >= codes.max_uses) {
        setDiscountError('This code has reached its maximum usage limit');
        setApplyingDiscount(false);
        return;
      }

      // Check if user already used this code
      const { data: usage } = await supabase
        .from('discount_code_usage')
        .select('id')
        .eq('code_id', codes.id)
        .eq('user_id', user.id)
        .single();

      if (usage) {
        setDiscountError('You have already used this discount code');
        setApplyingDiscount(false);
        return;
      }

      // Apply discount
      setAppliedDiscount({
        code: codes.code,
        type: codes.discount_type,
        value: codes.discount_value
      });
      setDiscountError('');
      setApplyingDiscount(false);
    } catch (err) {
      console.error('Error applying discount:', err);
      setDiscountError('Failed to validate discount code. Please try again.');
      setApplyingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
  };

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const finalPrice = calculateFinalPrice();

      // If discount makes it free, skip payment
      if (finalPrice === 0 && appliedDiscount) {
        // Record the discount usage
        const { data: discountData } = await supabase
          .from('discount_codes')
          .select('id')
          .eq('code', appliedDiscount.code)
          .single();

        if (discountData) {
          await supabase.from('discount_code_usage').insert({
            code_id: discountData.id,
            user_id: user.id,
            purchase_type: 'provider_subscription'
          });

          await supabase
            .from('discount_codes')
            .update({ times_used: supabase.rpc('increment', { x: 1 }) })
            .eq('id', discountData.id);
        }

        window.location.reload();
        return;
      }

      const { data, error: functionError } = await supabase.functions.invoke(
        'create-subscription-checkout',
        {
          body: { 
            discountCode: appliedDiscount?.code || undefined
          }
        }
      );

      if (functionError) throw functionError;
      if (!data?.url) throw new Error('No checkout URL received');

      window.location.href = data.url;
    } catch (err) {
      console.error('Payment error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process payment');
      setLoading(false);
    }
  };

  const calculateFinalPrice = () => {
    if (!appliedDiscount) return amount;

    if (appliedDiscount.type === 'percentage') {
      return amount * (1 - appliedDiscount.value / 100);
    } else {
      return Math.max(0, amount - appliedDiscount.value);
    }
  };

  const finalPrice = calculateFinalPrice();
  const discountAmount = amount - finalPrice;

  return (
    <div className="space-y-6">
      {/* Workwear Purchase Required Banner */}
      {!workwearPurchased && (
        <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl shadow-lg p-6 animate-pulse">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-alert-line text-2xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold mb-2">Action Required: Purchase Workwear</h3>
              <p className="text-white/90 mb-4">
                You must purchase the Taskbudy starter workwear kit (£{(WORKWEAR_WITH_VAT / 100).toFixed(2)} inc. VAT) before you can accept jobs. All providers must wear branded workwear.
              </p>
              <button
                onClick={onWorkwearPurchase}
                className="bg-white text-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-shopping-cart-line"></i>
                Purchase Workwear Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <i className="ri-error-warning-line text-red-600 text-xl flex-shrink-0"></i>
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Yearly Subscription Plan */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Subscription Plan
        </label>
        <div className="p-6 rounded-lg border-2 border-teal-500 bg-teal-50 relative">
          <div className="absolute -top-3 -right-3 bg-red-500 text-white text-xs px-3 py-1.5 rounded-full font-bold whitespace-nowrap shadow-lg">
            Save 20%
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xl font-bold text-gray-900 mb-1">Yearly Subscription</div>
              <div className="text-sm text-gray-600">
                <span className="line-through">£{pricing.original}</span> per year
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-teal-600">£{pricing.yearly}</div>
              <div className="text-xs text-gray-500 mt-1">before VAT</div>
            </div>
          </div>
        </div>
      </div>

      {/* Discount Code Input */}
      {!appliedDiscount && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Discount Code (Optional)
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <i className="ri-coupon-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                value={discountCode}
                onChange={(e) => {
                  setDiscountCode(e.target.value.toUpperCase());
                  setDiscountError('');
                }}
                placeholder="Enter discount code"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm uppercase"
                disabled={applyingDiscount}
              />
            </div>
            <button
              onClick={handleApplyDiscount}
              disabled={applyingDiscount || !discountCode.trim()}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap text-sm font-medium transition-colors"
            >
              {applyingDiscount ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Applying...
                </>
              ) : (
                'Apply'
              )}
            </button>
          </div>
          {discountError && (
            <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
              <i className="ri-error-warning-line"></i>
              {discountError}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-2">
            <i className="ri-information-line mr-1"></i>
            Have a discount code? Enter it above and click Apply to see your savings
          </p>
        </div>
      )}

      {/* Applied Discount Success */}
      {appliedDiscount && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex gap-3">
              <i className="ri-check-line text-green-600 text-xl flex-shrink-0"></i>
              <div className="text-sm">
                <p className="font-medium text-green-900">Discount Applied!</p>
                <p className="text-green-800">
                  Code <strong>{appliedDiscount.code}</strong> - Saving £{discountAmount.toFixed(2)}
                </p>
              </div>
            </div>
            <button
              onClick={handleRemoveDiscount}
              className="text-red-500 hover:text-red-700 text-sm font-medium"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>
      )}

      {/* Price Breakdown */}
      <div className="bg-slate-50 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-lg text-gray-900 mb-4">Price Breakdown</h3>
        <div className="space-y-3">
          <div className="flex justify-between text-gray-700">
            <span>{trade} - Yearly Subscription</span>
            <span className="font-medium">£{amount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-gray-700">
            <span>VAT (20%)</span>
            <span className="font-medium">£{vat.toFixed(2)}</span>
          </div>
          {appliedDiscount && discountAmount > 0 && (
            <div className="flex justify-between text-green-600 font-medium">
              <span className="flex items-center gap-1">
                <i className="ri-discount-percent-line"></i>
                Discount ({appliedDiscount.code})
              </span>
              <span>-£{discountAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="border-t border-gray-300 pt-3 flex justify-between text-lg font-bold text-gray-900">
            <span>Total</span>
            <span className="text-teal-600">£{(total - discountAmount).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">What's Included:</h3>
        <ul className="space-y-2">
          {[
            'Access to all job opportunities',
            'Professional profile on our platform',
            'Direct client communication',
            'Secure payment processing',
            'Marketing and promotion',
            '24/7 support'
          ].map((benefit, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
              <i className="ri-check-line text-teal-500 text-lg flex-shrink-0"></i>
              <span>{benefit}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Payment Button */}
      <button
        onClick={handlePayment}
        disabled={loading}
        type="button"
        className="w-full px-6 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg font-bold text-lg hover:from-teal-600 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer shadow-lg"
      >
        {loading ? (
          <>
            <i className="ri-loader-4-line animate-spin mr-2"></i>
            Processing...
          </>
        ) : finalPrice === 0 ? (
          <>
            <i className="ri-check-line mr-2"></i>
            Activate Account (Free)
          </>
        ) : (
          <>
            <i className="ri-secure-payment-line mr-2"></i>
            Pay £{(total - discountAmount).toFixed(2)} Now
          </>
        )}
      </button>

      <p className="text-xs text-gray-500 text-center mt-4">
        Secure payment powered by Stripe. Your payment information is encrypted and secure.
      </p>
    </div>
  );
};

export default SubscriptionPaymentSection;